<script setup lang="ts">
import { SharedMarketType } from '@shared/types'
import { Status, StatusType, BigNumberInBase } from '@injectivelabs/utils'
import { formatFundingRate } from '@shared/transformer/market/fundingRate'
import {
  UI_DEFAULT_MIN_DISPLAY_DECIMALS,
  UI_DEFAULT_FUNDING_RATE_DECIMALS
} from '@/app/utils/constants'
import { MarkPriceStatusKey, PerpetualMarketCyTags } from '@/types'
import type { UiMarketWithToken } from '@/types'
import type { PerpetualMarket } from '@injectivelabs/sdk-ts'

const sharedDerivativeStore = useSharedDerivativeStore()

const markPriceStatus = inject(
  MarkPriceStatusKey,
  new Status(StatusType.Loading)
)

const props = withDefaults(
  defineProps<{
    market: UiMarketWithToken
  }>(),
  {}
)

const { markPrice } = useDerivativeLastPrice(computed(() => props.market))

const now = useNow({ interval: 1000 })

const countdown = computed(() => {
  const nowTimestamp = Math.floor(now.value.getTime() / 1000)
  const secondsInHour = 3600
  const secondsSinceEpoch = nowTimestamp
  const secondsToNextEpochHour =
    secondsInHour - (secondsSinceEpoch % secondsInHour)

  const hours = Math.floor(secondsToNextEpochHour / secondsInHour)
  const minutes = Math.floor((secondsToNextEpochHour % secondsInHour) / 60)
  const seconds = secondsToNextEpochHour % 60

  return [hours, minutes, seconds]
    .map((value) => value.toString().padStart(2, '0'))
    .join(':')
})

const {
  valueToFixed: fundingRateToFixed,
  valueToBigNumber: fundingRateToBigNumber
} = useSharedBigNumberFormatter(
  computed(() => {
    const market = props.market as PerpetualMarket

    return formatFundingRate({
      info: market.perpetualMarketInfo,
      funding: market.perpetualMarketFunding
    })
  }),
  {
    roundingMode: BigNumberInBase.ROUND_DOWN,
    decimalPlaces: UI_DEFAULT_FUNDING_RATE_DECIMALS
  }
)

const { valueToString: annualizedFundingRateToString } =
  useSharedBigNumberFormatter(
    computed(() => {
      const hoursInYear = 365 * 24

      return new BigNumberInBase(fundingRateToFixed.value).times(hoursInYear)
    }),
    {
      roundingMode: BigNumberInBase.ROUND_DOWN,
      decimalPlaces: UI_DEFAULT_MIN_DISPLAY_DECIMALS
    }
  )

// const {
//   valueToFixed: openInterestToFixed,
//   valueToBigNumber: openInterestBigNumber
// } = useSharedBigNumberFormatter(
//   computed(
//     () =>
//       new BigNumberInBase(
//         derivativeStore.tickerOpenInterestMap[props.market.ticker] || 0
//       )
//   )
// )

const isExpiryMarket = computed(
  () => props.market.subType === SharedMarketType.Futures
)

watch(countdown, (countdown) => {
  if (countdown === '00:00:01') {
    sharedDerivativeStore.fetchMarketsSummary()
  }
})
</script>

<template>
  <PartialsTradeStatsHeaderItem class="lg:hidden xl:flex">
    <template #title>
      <CommonHeaderTooltip
        v-bind="{
          textColorClass: 'text-coolGray-400',
          tooltip: $t('trade.stats.markPriceTooltip'),
          popper: { strategy: 'fixed', placement: 'bottom' }
        }"
      >
        <span class="whitespace-nowrap">{{ $t('trade.markPrice') }}</span>
      </CommonHeaderTooltip>
    </template>

    <AppSpinner v-if="markPriceStatus.isLoading()" class="relative" is-sm />
    <SharedAmount
      v-else
      v-bind="{
        amount: markPrice,
        useSubscript: true,
        shouldAbbreviate: false,
        decimals: market.priceDecimals
      }"
    />
  </PartialsTradeStatsHeaderItem>

  <PartialsTradeStatsInfoCommon v-bind="{ market }" />

  <PartialsTradeStatsHeaderItem v-if="!isExpiryMarket">
    <template #title>
      <CommonHeaderTooltip
        v-bind="{
          textColorClass: 'text-coolGray-400',
          tooltip: $t('trade.stats.fundingRateTooltip'),
          popper: { strategy: 'fixed', placement: 'bottom' }
        }"
      >
        <span class="whitespace-nowrap">{{
          $t('trade.stats.fundingOrCountdown')
        }}</span>
      </CommonHeaderTooltip>
    </template>

    <div class="flex gap-3 justify-end items-center">
      <div v-if="!fundingRateToBigNumber.isNaN()">
        <AppTooltip
          :ui="{
            width: 'w-auto',
            popper: {
              placement: 'bottom'
            }
          }"
          :content="`${$t('trade.stats.annualized')}: ${
            fundingRateToBigNumber.gt(0) ? '+' : ''
          }${annualizedFundingRateToString}%`"
        >
          <span
            :class="{
              'text-green-500': fundingRateToBigNumber.gte(0),
              'text-red-500': fundingRateToBigNumber.lt(0)
            }"
            class="cursor-pointer flex"
          >
            <SharedAmount
              v-bind="{
                useSubscript: true,
                shouldAbbreviate: false,
                cyValue: fundingRateToFixed,
                amount: fundingRateToFixed,
                decimals: UI_DEFAULT_FUNDING_RATE_DECIMALS,
                dataCy: dataCyTag(
                  PerpetualMarketCyTags.TradeStatsInfoFundingRate
                )
              }"
            >
              <template #prefix>
                <span> {{ fundingRateToBigNumber.gt(0) ? '+' : '' }}</span>
              </template>
            </SharedAmount>
            <span>%</span>
          </span>
        </AppTooltip>
      </div>
      <span v-else> &mdash; </span>

      <p class="tabular-nums">
        {{ countdown }}
      </p>
    </div>
  </PartialsTradeStatsHeaderItem>
</template>
